import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, CheckCircle2, Clock, Gamepad2, Loader2, Lock, Mail, Shield } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ParticleField } from "@/components/ParticleField";

const EMPTY = { identifier: "", password: "", ign: "", email: "" };
const INPUT = "pl-10 bg-white/5 border-white/10 text-white placeholder:text-slate-600 focus-visible:ring-[#8B5CF6]";

export default function LoginPage() {
  const { loginUser } = useStore();
  const navigate = useNavigate();
  const [view, setView] = useState("login"); // login | register | forgot | forgot-sent | pending
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (view === "forgot") {
        await api.post("/auth/forgot-password", { email: form.identifier });
        setView("forgot-sent");
        return;
      }
      const { data } =
        view === "login"
          ? await api.post("/auth/login", { email: form.identifier, password: form.password })
          : await api.post("/auth/register", {
              ign: form.ign,
              password: form.password,
              email: form.email.trim() || null,
            });
      if (view === "register" && data.pending) {
        setView("pending");
        return;
      }
      loginUser(data.access_token, data.user);
      toast.success(view === "login" ? `Welcome back, ${data.user.ign || data.user.email}` : "Account created", {
        description: data.user.role !== "member" ? `Role: ${data.user.role.toUpperCase()}` : undefined,
      });
      navigate("/");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <main data-testid="login-page" className="relative min-h-screen bg-[#05030A] flex items-center justify-center px-4 overflow-hidden">
      <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse 55% 45% at 50% 35%, rgba(139,92,246,0.18), transparent 70%)" }} />
      <ParticleField className="absolute inset-0" />

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        className="relative z-10 w-full max-w-sm pt-16"
      >
        <Link
          to="/"
          data-testid="login-back-link"
          className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-white transition-colors duration-300 mb-6"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back to store
        </Link>

        <div className="rounded-2xl border border-white/10 bg-[#0D0B12]/90 backdrop-blur-xl p-8">
          <div className="flex items-center gap-2 justify-center">
            <Shield className="w-6 h-6 text-[#8B5CF6]" strokeWidth={2.5} />
            <span className="font-display text-2xl tracking-tight text-white">ULTI<span className="text-[#A78BFA]">SMP</span></span>
          </div>
          <p className="mt-2 text-center text-xs tracking-[0.3em] text-slate-500">
            {view === "forgot" || view === "forgot-sent" ? "RESET PASSWORD" : "MEDIA / STAFF"}
          </p>

          {view === "pending" ? (
            <div className="mt-8 text-center" data-testid="register-pending">
              <Clock className="w-12 h-12 text-amber-400 mx-auto" strokeWidth={1.5} />
              <h2 className="mt-4 font-display text-xl text-white">Awaiting approval</h2>
              <p className="mt-2 text-sm text-slate-300 leading-relaxed">
                Your account <span className="text-[#A78BFA] font-semibold">{form.ign}</span> was created. It goes live once the owner approves it — check back soon.
              </p>
              <button onClick={() => setView("login")} data-testid="pending-back-to-login" className="mt-6 text-xs font-bold tracking-[0.15em] text-[#A78BFA] hover:text-white transition-colors duration-300">
                BACK TO LOGIN
              </button>
            </div>
          ) : view === "forgot-sent" ? (
            <div className="mt-8 text-center" data-testid="forgot-sent">
              <CheckCircle2 className="w-12 h-12 text-[#8B5CF6] mx-auto" strokeWidth={1.5} />
              <p className="mt-4 text-sm text-slate-300 leading-relaxed">
                If an account exists for <span className="text-[#A78BFA] font-semibold">{form.identifier}</span>, a reset link is on its way. The link expires in 1 hour.
              </p>
              <button onClick={() => setView("login")} data-testid="forgot-back-to-login" className="mt-6 text-xs font-bold tracking-[0.15em] text-[#A78BFA] hover:text-white transition-colors duration-300">
                BACK TO LOGIN
              </button>
            </div>
          ) : view === "forgot" ? (
            <form onSubmit={submit} className="mt-8 space-y-4">
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input type="email" required value={form.identifier} onChange={(e) => setForm({ ...form, identifier: e.target.value })} placeholder="Your account email" data-testid="forgot-email-input" className={INPUT} />
              </div>
              {error && <p className="text-sm text-red-400" data-testid="auth-error">{error}</p>}
              <button type="submit" disabled={loading} data-testid="forgot-submit-button" className="w-full py-3 rounded-xl bg-[#8B5CF6] hover:bg-[#7C3AED] disabled:opacity-60 text-white text-sm font-bold tracking-[0.15em] transition-[background-color,opacity] duration-300 flex items-center justify-center gap-2">
                {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                SEND RESET LINK
              </button>
              <button type="button" onClick={() => { setView("login"); setError(""); }} data-testid="forgot-cancel-button" className="w-full text-xs font-bold tracking-[0.15em] text-slate-500 hover:text-white transition-colors duration-300">
                BACK TO LOGIN
              </button>
            </form>
          ) : (
            <Tabs value={view} onValueChange={(v) => { setView(v); setError(""); }} className="mt-6">
              <TabsList className="w-full bg-white/5 border border-white/10">
                <TabsTrigger value="login" data-testid="auth-tab-login" className="flex-1 data-[state=active]:bg-[#8B5CF6] data-[state=active]:text-white">Login</TabsTrigger>
                <TabsTrigger value="register" data-testid="auth-tab-register" className="flex-1 data-[state=active]:bg-[#8B5CF6] data-[state=active]:text-white">Register</TabsTrigger>
              </TabsList>

              <form onSubmit={submit} className="mt-5 space-y-4">
                {view === "login" ? (
                  <div className="relative">
                    <Gamepad2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                    <Input required value={form.identifier} onChange={(e) => setForm({ ...form, identifier: e.target.value })} placeholder="IGN or email" data-testid="auth-identifier-input" className={INPUT} />
                  </div>
                ) : (
                  <>
                    <div className="relative">
                      <Gamepad2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                      <Input required value={form.ign} onChange={(e) => setForm({ ...form, ign: e.target.value })} placeholder="Minecraft username (IGN)" data-testid="auth-ign-input" className={INPUT} />
                    </div>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                      <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email (optional — for password resets)" data-testid="auth-email-input" className={INPUT} />
                    </div>
                  </>
                )}
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <Input type="password" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Password" data-testid="auth-password-input" className={INPUT} />
                </div>
                {view === "login" && (
                  <div className="text-right -mt-1">
                    <button type="button" onClick={() => { setView("forgot"); setError(""); }} data-testid="forgot-password-link" className="text-xs text-slate-500 hover:text-[#A78BFA] transition-colors duration-300">
                      Forgot password?
                    </button>
                  </div>
                )}
                {error && <p className="text-sm text-red-400" data-testid="auth-error">{error}</p>}
                <button type="submit" disabled={loading} data-testid="auth-submit-button" className="w-full py-3 rounded-xl bg-[#8B5CF6] hover:bg-[#7C3AED] disabled:opacity-60 text-white text-sm font-bold tracking-[0.15em] transition-[background-color,opacity] duration-300 flex items-center justify-center gap-2">
                  {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                  {view === "login" ? "LOGIN" : "CREATE ACCOUNT"}
                </button>
                {view === "register" && (
                  <p className="text-[11px] text-center text-slate-600">Just your IGN and a password. New accounts go live once the owner approves them.</p>
                )}
              </form>
            </Tabs>
          )}
        </div>
      </motion.div>
    </main>
  );
}
